import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { List, X, SignIn, UserPlus, SignOut, Lock, SquaresFour } from '@phosphor-icons/react';

// Bundled locally (public/logo.png) so ad-blockers and strict corporate
// networks that filter `customer-assets.emergentagent.com` still render it.
const LOGO_URL = '/logo.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Exam Prep', path: '/exam-prep' },
  { label: 'For Students', path: '/for-students' },
  { label: 'For Recruiters', path: '/for-recruiters' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Study Alliance" className="h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.path)
                    ? 'text-blue-700 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user && (
              <Link
                to={user.role === 'admin' ? '/admin' : user.role === 'institution' ? '/institution' : '/recruiter'}
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-blue-700 text-white px-4 py-2 rounded-md hover:bg-blue-800 transition-colors shadow-sm"
                data-testid="nav-dashboard-btn"
              >
                <SquaresFour size={16} weight="fill" /> Dashboard
              </Link>
            )}
            {!user && user !== null && (
              <>
                <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2" data-testid="nav-login">
                  <SignIn size={16} /> Login
                </Link>
                <Link to="/recruiter-signup" className="inline-flex items-center gap-1.5 text-sm font-medium bg-blue-700 text-white px-4 py-2 rounded-md hover:bg-blue-800 transition-colors" data-testid="nav-recruiter-signup">
                  <UserPlus size={16} /> Recruiter Sign Up
                </Link>
              </>
            )}
            {user && (
              <>
                <Link to="/change-password" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 px-3 py-2" data-testid="nav-change-password" title="Change password">
                  <Lock size={16} />
                </Link>
                <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 px-3 py-2" data-testid="nav-logout">
                  <SignOut size={16} /> Logout
                </button>
              </>
            )}
          </div>

          <button className="lg:hidden p-2 text-gray-600" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="nav-mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setOpen(false)}
                className={`block px-3 py-2.5 text-sm font-medium rounded-md ${isActive(link.path) ? 'text-blue-700 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'}`}>
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-100 space-y-1">
              {user && (
                <Link
                  to={user.role === 'admin' ? '/admin' : user.role === 'institution' ? '/institution' : '/recruiter'}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2.5 text-sm font-semibold bg-blue-700 text-white text-center rounded-md"
                  data-testid="nav-mobile-dashboard-btn"
                >
                  Dashboard
                </Link>
              )}
              {!user && user !== null && (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-600">Login</Link>
                  <Link to="/recruiter-signup" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-medium bg-blue-700 text-white text-center rounded-md">Recruiter Sign Up</Link>
                </>
              )}
              {user && <button onClick={() => { logout(); setOpen(false); }} className="w-full text-left px-3 py-2.5 text-sm font-medium text-gray-600">Logout</button>}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
